import React, { useState } from 'react';
import PublicLayout from '../components/PublicLayout';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <PublicLayout theme="light">
      <div className="public-page">
        {/* Hero Section */}
        <div className="public-page-header">
          <h1>Get in Touch</h1>
          <p>
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <div className="public-page-content">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div className="dashboard-card">
                <Mail className="w-8 h-8 mb-3" style={{color: 'var(--gold)'}} />
                <h3>Contact Us</h3>
                <a href="mailto:info@oursuccessclub.com" className="font-semibold" style={{color: 'var(--gold)'}}>
                  info@oursuccessclub.com
                </a>
                <p className="text-sm mt-2" style={{color: 'var(--muted)'}}>For all inquiries</p>
              </div>

              <div className="dashboard-card" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C29A4B 100%)', color: '#fff'}}>
                <h3 style={{color: '#fff'}}>Response Time</h3>
                <p className="text-sm" style={{color: 'rgba(255,255,255,0.9)'}}>
                  We typically respond within 24 hours during business days.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="dashboard-card">
                {submitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for contacting us. We'll get back to you shortly.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-amber-600 hover:text-amber-700 font-semibold"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-7">
                    <div>
                      <label htmlFor="name" className="block font-bold text-gray-900 mb-3" style={{fontSize: '1.05rem', letterSpacing: '0.5px'}}>
                        YOUR NAME *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 rounded-xl transition-all text-gray-900 font-medium"
                        style={{
                          backgroundColor: '#ffffff',
                          borderColor: '#cbd5e1',
                          fontSize: '1rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                        }}
                        placeholder="John Doe"
                        onFocus={(e) => {
                          e.target.style.borderColor = '#D4AF37';
                          e.target.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.15)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#cbd5e1';
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                        }}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block font-bold text-gray-900 mb-3" style={{fontSize: '1.05rem', letterSpacing: '0.5px'}}>
                        EMAIL ADDRESS *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 rounded-xl transition-all text-gray-900 font-medium"
                        style={{
                          backgroundColor: '#ffffff',
                          borderColor: '#cbd5e1',
                          fontSize: '1rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                        }}
                        placeholder="john@example.com"
                        onFocus={(e) => {
                          e.target.style.borderColor = '#D4AF37';
                          e.target.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.15)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#cbd5e1';
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                        }}
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block font-bold text-gray-900 mb-3" style={{fontSize: '1.05rem', letterSpacing: '0.5px'}}>
                        SUBJECT *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border-2 rounded-xl transition-all text-gray-900 font-medium"
                        style={{
                          backgroundColor: '#ffffff',
                          borderColor: '#cbd5e1',
                          fontSize: '1rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                        }}
                        placeholder="How can we help?"
                        onFocus={(e) => {
                          e.target.style.borderColor = '#D4AF37';
                          e.target.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.15)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#cbd5e1';
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                        }}
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block font-bold text-gray-900 mb-3" style={{fontSize: '1.05rem', letterSpacing: '0.5px'}}>
                        MESSAGE *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-5 py-4 border-2 rounded-xl transition-all resize-none text-gray-900 font-medium"
                        style={{
                          backgroundColor: '#ffffff',
                          borderColor: '#cbd5e1',
                          fontSize: '1rem',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                          minHeight: '160px'
                        }}
                        placeholder="Tell us more about your inquiry..."
                        onFocus={(e) => {
                          e.target.style.borderColor = '#D4AF37';
                          e.target.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.15)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#cbd5e1';
                          e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                        }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 px-6 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold rounded-xl hover:from-amber-700 hover:to-amber-600 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-base uppercase tracking-wide"
                      style={{letterSpacing: '1px'}}
                    >
                      {loading ? (
                        <span>Sending...</span>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ContactUs;